import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { AuthSplitLayout } from '../components/ui/auth-split-layout';
import SeoHead from '../components/SeoHead';
import { db } from '../firebase';
import { COUNTRIES, citiesForCountry, PHONE_DIAL_CODES } from '../data/countriesCities';
import { normalizeEmail } from '../utils/subscriptions';
import { notifyAdminStudentEnrolled } from '../utils/notifications';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phoneCode: '+92',
  phone: '',
  dob: '',
  country: '',
  city: '',
  password: '',
  confirmPassword: '',
};

export default function SignUpPage() {
  const { currentUser, signUp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cities = useMemo(() => citiesForCountry(form.country), [form.country]);

  if (currentUser) return <Navigate to="/portal" replace />;

  function updateField(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'country') next.city = '';
      return next;
    });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = normalizeEmail(form.email);
    const phone = form.phone.replace(/[^\d]/g, '');
    const phoneCode = form.phoneCode;
    const dob = form.dob;
    const country = form.country;
    const city = form.city;
    const password = form.password;
    const confirmPassword = form.confirmPassword;

    if (
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !dob ||
      !country ||
      !city ||
      !password ||
      !confirmPassword
    ) {
      setError('Please complete all enrollment fields.');
      return;
    }
    if (phone.length < 7) {
      setError('Please enter a valid phone number.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Password and confirm password do not match.');
      return;
    }

    const displayName = `${firstName} ${lastName}`.trim();
    setLoading(true);
    setError('');
    try {
      const cred = await signUp(email, password, displayName);
      const user = cred?.user;
      if (user?.uid) {
        await setDoc(
          doc(db, 'users', user.uid),
          {
            email,
            emailLower: email,
            displayName,
            firstName,
            lastName,
            phoneCountryCode: phoneCode,
            phone: `${phoneCode}${phone}`,
            dob,
            country,
            city,
            role: 'student',
            accessStatus: 'pending_payment',
            profileComplete: false,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
        await notifyAdminStudentEnrolled({
          studentName: displayName,
          email,
          registrationNumber: '',
          unpaid: true,
        }).catch(console.warn);
      }
      navigate('/portal', { replace: true });
    } catch (err) {
      console.error(err);
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in.');
      } else if (code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else {
        setError(err?.message || 'Enrollment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <SeoHead />
      <AuthSplitLayout>
        <div className="mb-6">
          <h1>Create your account</h1>
          <p className="sba-split-lead">
            Already enrolled?{' '}
            <button type="button" onClick={() => navigate('/login')} className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in
            </button>
          </p>
        </div>

        {error ? <p className="sba-split-error">{error}</p> : null}

        <form onSubmit={handleSubmit} className="space-y-6" style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label className="sba-split-field">
              First name
              <input
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="sba-split-field">
              Last name
              <input
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                autoComplete="family-name"
                required
              />
            </label>
          </div>

          <label className="sba-split-field">
            Email Address
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              autoComplete="email"
              required
            />
          </label>

          <label className="sba-split-field">
            Phone number
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <select
                value={form.phoneCode}
                onChange={(e) => updateField('phoneCode', e.target.value)}
                aria-label="Country code"
              >
                {PHONE_DIAL_CODES.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="3XX XXXXXXX"
                autoComplete="tel-national"
                required
              />
            </div>
          </label>

          <label className="sba-split-field">
            Date of birth
            <input type="date" value={form.dob} onChange={(e) => updateField('dob', e.target.value)} required />
          </label>

          <label className="sba-split-field">
            Country
            <select value={form.country} onChange={(e) => updateField('country', e.target.value)} required>
              <option value="">Select country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="sba-split-field">
            City
            <select
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
              disabled={!form.country}
              required
            >
              <option value="">{form.country ? 'Select city' : 'Select country first'}</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </label>

          <label className="sba-split-field">
            Password
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          <label className="sba-split-field">
            Confirm password
            <input
              type="password"
              value={form.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          <button type="submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Sign up'}
          </button>
        </form>
      </AuthSplitLayout>
    </>
  );
}
